import { PageType } from "@/services/shared-models";
import { useDataStore } from "@/stores";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ArrowRightIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import NavButton from "./components/NavButton";
import NavStep from "./components/NavStep";
import Notification from "./components/Notification/Notification";

const NavBar = () => {
    const pages = useDataStore((state) => state.pages);

    const { t } = useTranslation(["header"]);

    const { locale } = useParams();

    const renderStepLinks = () => {
        const steps = pages.filter((page) => page.pageType.startsWith("Step"));
        if (steps.length === 0) return null;

        return (
            <div className="flex items-center bg-primary-100 rounded-full gap-0.5 w-fit h-[38px]">
                {steps
                    .sort((a, b) => a.pageType.localeCompare(b.pageType))
                    .map((step, index) => {
                        const isLastStep = step === steps[steps.length - 1];

                        return (
                            <Fragment key={step.id}>
                                <NavStep link={`/${locale}/${step.slug}`} name={step.title} step={`${index + 1}`} />
                                {!isLastStep && <ArrowRightIcon className="size-3  text-primary-500" />}
                            </Fragment>
                        );
                    })}
            </div>
        );
    };

    const renderElementListLink = () => {
        const elements = pages.find((page) => page.pageType === PageType.Elements);
        if (!elements) return null;

        return <NavButton link={`/${locale}/${elements.slug}`} name={elements.title} />;
    };

    const renderDownloadsLink = () => {
        const downloads = pages.find((page) => page.pageType === PageType.Download);
        if (!downloads) return null;

        return <NavButton link={`/${locale}/${downloads.slug}`} name={downloads.title} />;
    };

    const renderExtraMenu = () => {
        const visiblePages = pages.filter((page) => !page.hidden);
        const groups = visiblePages.filter((page) => page.pageType === PageType.Content && !page.parentPageId);
        if (groups.length === 0) return null;

        return (
            <Popover>
                <PopoverButton
                    className={
                        "group h-[38px] focus:outline-0 ring-0 rounded-full px-4 py-2 flex items-center space-x-2 hover:bg-primary-600 cursor-pointer hover:text-white font-medium"
                    }
                >
                    {t("navigation.more", { ns: "header" })}
                    <ChevronDownIcon aria-hidden="true" className="ml-1 size-5 opacity-50 group-hover:opacity-100" />
                </PopoverButton>
                <PopoverPanel
                    transition
                    className="absolute top-[118px] flex left-0 w-full transition data-closed:-translate-y-1 data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in z-[1000]"
                >
                    <div className="flex container 2xl:px-[108px] mx-auto">
                        <div
                            className={clsx([
                                "grid mx-auto text-sm/6 z-10 [--anchor-gap:4px] p-1 rounded-lg bg-white ring-1 shadow-lg ring-gray-900/0 border border-gray-200",
                                groups.length === 1 && "grid-cols-1",
                                groups.length === 2 && "grid-cols-2",
                                groups.length === 3 && "grid-cols-2 md:grid-cols-3",
                                groups.length === 4 && "grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
                                groups.length === 5 && "grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
                                groups.length >= 6 && "grid-cols-2 md:grid-cols-3 lg:grid-cols-6",
                            ])}
                        >
                            {groups
                                .sort((a, b) => a.parentOrder - b.parentOrder)
                                .map((group) => {
                                    const inverseOrder = group.inverseOrder || false;
                                    const children = visiblePages
                                        .filter((page) => page.parentPageId === group.id)
                                        .sort((a, b) => {
                                            if (inverseOrder) return b.parentOrder - a.parentOrder;
                                            return a.parentOrder - b.parentOrder;
                                        });

                                    if (children.length > 5) {
                                        children.splice(5, children.length - 5, {
                                            ...group,
                                            id: "view-more",
                                            title: t("approfondir.more", { ns: "header" }),
                                        });
                                    }

                                    return (
                                        <div
                                            key={group.id}
                                            className="flex flex-col first:rounded-tl-lg last:rounded-tr-lg"
                                        >
                                            <PopoverButton
                                                as={Link}
                                                to={`/${locale}/${group.url}`}
                                                className="text-sm/6 font-semibold uppercase block bg-[#f6f9fc] p-2 hover:underline"
                                            >
                                                {group.title}
                                            </PopoverButton>
                                            <div className="flex flex-col gap-2 p-2 ml-2 max-h-[400px] overflow-y-auto">
                                                {children.map((child) => (
                                                    <PopoverButton
                                                        as={Link}
                                                        key={child.id}
                                                        to={`/${locale}/${child.url}`}
                                                        className={clsx([
                                                            "group flex cursor-pointer items-center hover:underline",
                                                            child.id === "view-more" && "mt-3 italic",
                                                        ])}
                                                    >
                                                        {child.title}
                                                    </PopoverButton>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </PopoverPanel>
            </Popover>
        );
    };

    return (
        <>
            <div id="nav-bar" className="container mx-auto 2xl:px-[108px] flex items-center h-[40px]">
                <nav className="flex gap-1 space-x-2 shrink-0">
                    {renderStepLinks()}
                    {renderElementListLink()}
                    {renderDownloadsLink()}
                    {renderExtraMenu()}
                </nav>

                <Notification />
            </div>

            {/* <div className="visible 2xl:hidden bg-blue-50">
                <Notification />
            </div> */}
        </>
    );
};

export default NavBar;
